import "rxjs/Rx";
import { Injectable } from "@angular/core";
import { Actions, Effect, ROOT_EFFECTS_INIT } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { Observable } from "rxjs/Observable";
import { NotificationsService } from "angular2-notifications";
import { AuthenticationService } from "./authentication.service";
import * as AuthActions from "./auth.actions";
import {
  LoginAction,
  LoginFailAction,
  LoginSuccessAction,
  RecoverPasswordAction,
  RecoverPasswordFailAction,
  RecoverPasswordSuccessAction,
  RegisterAction,
  RegisterFailAction,
  RegisterSuccessAction,
  ResetPasswordAction,
  ResetPasswordFailAction,
  ResetPasswordSuccessAction,
} from "./auth.actions";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { LoadUserAction } from "@core/store/user/user.actions";
import { MatDialog } from "@angular/material";
import { WindowRefService } from "@core/services/window.service";
import { catchError, flatMap } from "rxjs/operators";
import { LogoutAction } from "./auth.actions";

/**
 * Auth Effects
 */
@Injectable()
export class AuthEffects {
  nativeWindow: any;

  @Effect()
  load_storage: Observable<Action> = this.actions$
    .ofType(ROOT_EFFECTS_INIT)
    // .startWith(new AuthActions.CheckAction())
    .flatMap(() => this.authService.isAuthenticated())
    .flatMap((isAuthenticated) => {
      if (!isAuthenticated) {
        // try to authenticate with refresh token
        return this.authService.refreshToken().pipe(
          flatMap(() => this.authService.isAuthenticated()),
          catchError(() => {
            // return Observable.throw(err);
            return Observable.of(false);
          }),
        );
      } else {
        return Observable.of(isAuthenticated);
      }
    })
    .switchMap((isAuthenticated: boolean) => {
      return this.authService
        .loadFromStorage()
        .map((user) => new AuthActions.LoginSuccessAction(user))
        .catch(() => Observable.of(new AuthActions.AuthNoAction()));
    });

  @Effect()
  login$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.LOGIN)
    .map((action: LoginAction) => action.payload)
    .switchMap((authData) => {
      return this.authService
        .login(authData.email, authData.password)
        .map((user) => new AuthActions.LoginSuccessAction(user))
        .catch((error) =>
          Observable.of(new AuthActions.LoginFailAction(error)),
        );
    });

  @Effect()
  login_success$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.LOGIN_SUCCESS)
    .map((action: LoginSuccessAction) => action.payload)
    .do((user) => {
      // redirect to dashboard
      // this.router.navigate(this.authService.getInterruptedUrl() ? [this.authService.getInterruptedUrl()] : ['dashboard']);

      // push user id to google analytics
      this.nativeWindow.dataLayer.push({
        userID: user.user_id,
      });

      setTimeout(() => {
        this.translate.use(user.lang);
      }, 100);
    })
    .map(() => {
      return new LoadUserAction();
    });

  @Effect()
  login_fail$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.LOGIN_FAIL)
    .map((action: LoginFailAction) => action.payload)
    .switchMap((error) => {
      return Observable.of(
        this.notificationService.error(
          this.translate.instant("login.notifications.login_fail.title"),
          this.translate.instant("login.notifications.login_fail.text"),
        ),
      );
    });

  @Effect({ dispatch: false })
  logout$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.LOGOUT)
    .do(() => {
      this.authService.logout();
      this.router.navigate(["login"]);
    });

  @Effect()
  register$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.REGISTER)
    .map((action: RegisterAction) => action.payload)
    .switchMap((registerData) => {
      return this.authService
        .register(registerData)
        .map((user) => new AuthActions.RegisterSuccessAction(user))
        .catch((error) =>
          Observable.of(new AuthActions.RegisterFailAction(error)),
        );
    });

  @Effect()
  register_success$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.REGISTER_SUCCESS)
    .map((action: RegisterSuccessAction) => action.payload)
    .do(() => {
      this.router.navigate(["signup/check-email"]);
    })
    .switchMap(() => {
      return Observable.of(
        this.notificationService.success(
          this.translate.instant("login.notifications.register_success.title"),
          this.translate.instant("login.notifications.register_success.text"),
        ),
      );
    });

  @Effect()
  register_fail$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.REGISTER_FAIL)
    .map((action: RegisterFailAction) => action.payload)
    .switchMap((result) => {
      return Observable.of(
        this.notificationService.error(
          this.translate.instant("login.notifications.register_fail.title"),
          this.translate.instant(this.getRegisterErrorMessage(result)),
        ),
      );
    });

  @Effect()
  reset_password$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.RESET_PASSWORD)
    .map((action: ResetPasswordAction) => action.payload)
    .switchMap((resetPasswordData) => {
      return this.authService
        .recoverPassword(resetPasswordData.email)
        .map((token) => new AuthActions.ResetPasswordSuccessAction(token))
        .catch((error) =>
          Observable.of(new AuthActions.ResetPasswordFailAction(error)),
        );
    });

  @Effect()
  reset_password_success$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.RESET_PASSWORD_SUCCESS)
    .map((action: ResetPasswordSuccessAction) => action.payload)
    .switchMap((data) => {
      return Observable.of(
        this.notificationService.success(
          this.translate.instant(
            "reset_password.notifications.reset_success.title",
          ),
          this.translate.instant(
            "reset_password.notifications.reset_success.text",
          ),
        ),
      );
    });

  @Effect()
  reset_password_fail$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.RESET_PASSWORD_FAIL)
    .map((action: ResetPasswordFailAction) => action.payload)
    .switchMap((res) => {
      return Observable.of(
        this.notificationService.error(
          this.translate.instant(
            "reset_password.notifications.reset_fail.title",
          ),
          this.translate.instant(
            "reset_password.notifications.reset_fail.text",
          ),
        ),
      );
    });

  @Effect()
  recover_password$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.RECOVER_PASSWORD)
    .map((action: RecoverPasswordAction) => action.payload)
    .switchMap((recoverPasswordData) => {
      return this.authService
        .resetPassword(recoverPasswordData.token, recoverPasswordData.password)
        .map((token) => new AuthActions.RecoverPasswordSuccessAction())
        .catch((error) =>
          Observable.of(new AuthActions.RecoverPasswordFailAction(error)),
        );
    });

  @Effect()
  recover_password_success$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.RECOVER_PASSWORD_SUCCESS)
    .map((action: RecoverPasswordSuccessAction) => action.payload)
    .switchMap((data) => {
      return Observable.of(
        this.notificationService.success(
          this.translate.instant(
            "recover_password.notifications.recover_success.title",
          ),
          this.translate.instant(
            "recover_password.notifications.recover_success.text",
          ),
        ),
      );
    })
    .do(() => {
      this.router.navigate(["login"]);
    });

  @Effect()
  recover_password_fail$: Observable<Action> = this.actions$
    .ofType(AuthActions.ActionTypes.RECOVER_PASSWORD_FAIL)
    .map((action: RecoverPasswordFailAction) => action.payload)
    .switchMap((error) => {
      return Observable.of(
        this.notificationService.error(
          this.translate.instant(
            "recover_password.notifications.recover_fail.title",
          ),
          this.translate.instant(error.error.message),
        ),
      );
    });

  constructor(
    private actions$: Actions,
    private authService: AuthenticationService,
    private notificationService: NotificationsService,
    private translate: TranslateService,
    private router: Router,
    private winRef: WindowRefService,
  ) {
    this.nativeWindow = winRef.getNativeWindow();
  }

  getRegisterErrorMessage(result) {
    // TODO: this should be improved, we are not covering all the errors

    if (result.status === 400) {
      switch (result.error.violations.email[0].type) {
        case "EMAIL_NOT_UNIQUE":
          return "login.notifications.register_fail.error.email_not_unique";
      }
    }

    return "notifications.unknown_error";
  }
}



// WEBPACK FOOTER //
// /app/src/app/core/store/auth/auth.effects.ts